import { useMemo } from "react"
import { useSelector } from "react-redux"
import {
    checkboxColDefCreator,
    companyAbsenceTypeColDefCreator,
    employeeColDefCreator,
    numberColDefCreator,
    projectColDefCreator,
    stringColDefCreator,
} from "./standard-col-def-creators"
import { TableIds } from "../../../common/table-identifier-utils"
import { Absence, ReduxState, tFeatures, TimekeepingEntry } from "../../../common/types"
import { tSourceData, tResourceObject, ResourceObject } from "../../../dashboard-data/types"
import { getObjectId } from "../../../common/ag-grid-ts-utils"
import { tGroupKeyInfo } from "../types"
import { getFlagEnabled } from "../../../getFlagValue"
import { EmployeeHolidayRecord, EmployeePTORecord, iCostCode } from "../../../cached-data/types"
import { IconPalmTree, IconPalmTreeFilled, iconSizeM } from "@rhumbix/rmbx_design_system_web"
import { selectPTOFulfillment, computePTOFulfillment } from "../../../selectors/pto"
import { TkSummary } from "../../../common/types"
import * as API from "../../../api"
import { buildPTORowsForEmployee, buildPTOCostCodesByBucket } from "../../modals/modal-create-time-card"

/** Core fields for a PTO-driven absence row, derived from the PTO record itself. */
export type AbsencePTORowFields = {
    employee: number
    date: string
    absence_type: number | null
}

/**
 * Core fields for a PTO-driven timekeeping-entry row, derived from the PTO record itself.
 * `pto_bucket_id` is always included so the cost-code cell editor can filter to eligible codes.
 */
export type TimekeepingPTORowFields = {
    employee: number
    date: string
    adjusted_minutes_st: number
    adjusted_minutes_ot: 0
    adjusted_minutes_dt: 0
    pto_bucket_id: number
}

export type PTORowFieldsResult =
    | { rowType: "absence"; fields: AbsencePTORowFields }
    | { rowType: "timekeeping"; fields: TimekeepingPTORowFields }

/**
 * Build the core row fields from a single PTO or holiday record, without any
 * context-specific fields (foreman, shift times, cost code, isPlaceholder, etc.).
 * Callers spread these fields into their own row objects and add whatever extras they need,
 * including whether the row should be marked as a placeholder.
 *
 * Rules:
 *   - ABSENCE_TYPE → absence fields with the bucket's linked absence type.
 *   - COST_CODE    → timekeeping fields with hours converted to rounded minutes and
 *                    `pto_bucket_id` preserved so the cost-code selector can filter.
 *
 * @param record - A single EmployeePTO or EmployeeHoliday record.
 * @returns A discriminated-union result whose `fields` can be spread into a row object.
 */
export const buildPTORowFields = (record: EmployeePTORecord | EmployeeHolidayRecord): PTORowFieldsResult => {
    if (record.pto_bucket_type === "ABSENCE_TYPE") {
        return {
            rowType: "absence",
            fields: {
                employee: record.employee,
                date: record.date,
                absence_type: record.absence_type_id ?? null,
            },
        }
    }
    return {
        rowType: "timekeeping",
        fields: {
            employee: record.employee,
            date: record.date,
            adjusted_minutes_st: Math.round((record.hours ?? 0) * 60),
            adjusted_minutes_ot: 0,
            adjusted_minutes_dt: 0,
            pto_bucket_id: record.pto_bucket_id,
        },
    }
}

/**
 * Extends employeesInContext with the employee from lockedColumns so that an employee with
 * PTO/holiday data but no TK rows is still considered in-context for the PTO Requests tab.
 * Kept separate from the main employeesInContext to avoid changing behavior for cohort
 * loading, CI/CO diff checks, and child component contexts.
 *
 * @param employeesInContext - Employee IDs already derived from source data rows
 * @param lockedColumns - The modal's locked column group-key info
 */
export const usePTORequestEmployeesFromContext = (
    employeesInContext: number[],
    lockedColumns: tGroupKeyInfo[] | null
): number[] => {
    return useMemo(() => {
        if (!getFlagEnabled("WA-9197-pto-accruals")) return []

        const lockedEmployee = lockedColumns?.find(c => c.colDef.field === "/employee")
        if (!lockedEmployee?.value) return employeesInContext
        const lockedId = getObjectId(lockedEmployee.value)
        return lockedId && !employeesInContext.includes(lockedId)
            ? [...employeesInContext, lockedId]
            : employeesInContext
    }, [employeesInContext, lockedColumns])
}

export type PTORequestsTableRow = ResourceObject<{
    /** The source request record — either an EmployeePTO or EmployeeHoliday. */
    request: EmployeePTORecord | EmployeeHolidayRecord
    /** The matched absence or TK entry that fulfills the request, or null if unfulfilled. */
    match: Absence | TimekeepingEntry | null
    /** Frontend-computed: true when a matching absence/TK entry was found in the current view. */
    fulfilled: boolean
    date: string
    work_shift_id: number | null
    absence_type: number | null
    cost_code: number | null
    /** Project ID from the matched TK entry; populated only for COST_CODE rows. */
    project: number | null
}>

/**
 * Builds the combined row data for the PTO Requests tab by joining employeePTO and employeeHolidays
 * (from Redux entities) with absences and timekeepingEntries (from the modal's local source data).
 *
 * Fulfillment is computed on the frontend: a record is considered fulfilled if a matching
 * absence (for ABSENCE_TYPE buckets) or TK entry (for COST_CODE buckets) is found in the
 * current view. All records are always shown; the fulfillment icon indicates their status.
 *
 * @param localSourceData - The modal's local source data (absences and timekeepingEntries)
 * @param employeesInContext - Array of employee IDs present in the open modal
 * @param date - The date string (YYYY-MM-DD) for the currently open modal
 */
export const usePTORequestsTableRows = (
    localSourceData: tSourceData,
    employeesInContext: number[],
    date: string | null | undefined
): tResourceObject[] => {
    const employeePTO = useSelector((state: ReduxState) => state.entities.employeePTO?.objects ?? {})
    const employeeHolidays = useSelector((state: ReduxState) => state.entities.employeeHolidays?.objects ?? {})
    const costCodes = useSelector((state: ReduxState) => state.entities.costCodes?.objects ?? {})
    const ptoFulfillment = useSelector(selectPTOFulfillment)

    return useMemo(() => {
        if (!getFlagEnabled("WA-9197-pto-accruals") || !date) return []

        const employeeSet = new Set(employeesInContext)
        const absences = (localSourceData.absences ?? []) as ResourceObject<Absence>[]
        const timekeepingEntries = (localSourceData.timekeepingEntries ?? []) as ResourceObject<TimekeepingEntry>[]

        // Build lookup: "employeeId-date" → absences for that employee+date
        const absencesByEmployeeDate = new Map<string, ResourceObject<Absence>[]>()
        absences.forEach(absence => {
            const employeeId = getObjectId(absence.employee)
            if (!employeeSet.has(employeeId)) return
            const key = `${employeeId}-${absence.date}`
            const existing = absencesByEmployeeDate.get(key) ?? []
            existing.push(absence)
            absencesByEmployeeDate.set(key, existing)
        })

        // Build lookup: "employeeId-date" → all TK entries for that employee+date
        const tkEntriesByEmployeeDate = new Map<string, ResourceObject<TimekeepingEntry>[]>()
        timekeepingEntries.forEach(entry => {
            const employeeId = getObjectId(entry.employee)
            if (!employeeSet.has(employeeId)) return
            const key = `${employeeId}-${entry.date}`
            const existing = tkEntriesByEmployeeDate.get(key) ?? []
            existing.push(entry)
            tkEntriesByEmployeeDate.set(key, existing)
        })

        const ptoRecords = Object.values(employeePTO).filter(r => employeeSet.has(r.employee) && r.date === date)
        const holidayRecords = Object.values(employeeHolidays).filter(
            r => employeeSet.has(r.employee) && r.date === date
        )

        const rows = [...ptoRecords, ...holidayRecords].reduce((rows, record) => {
            const key = `${record.employee}-${record.date}`

            if (record.pto_bucket_type === "ABSENCE_TYPE") {
                const matchedAbsence = (absencesByEmployeeDate.get(key) ?? []).find(
                    a => getObjectId(a.absence_type) === record.absence_type_id
                )
                rows.push({
                    request: record,
                    match: matchedAbsence ?? null,
                    fulfilled: !!matchedAbsence || !!ptoFulfillment[String(record.id)],
                    date: record.date,
                    cost_code: null,
                    project: null,
                    absence_type: matchedAbsence?.absence_type ?? record.absence_type_id ?? null,
                    work_shift_id: matchedAbsence?.work_shift_id ?? null,
                    gridId: record.id,
                })
            } else {
                // COST_CODE: find a TK entry whose cost code maps to the same PTO bucket and whose
                // ST minutes meet or exceed the requested hours. Compare in integer minutes to avoid
                // floating-point precision issues.
                const matchedEntry = (tkEntriesByEmployeeDate.get(key) ?? []).find(e => {
                    const ccId = getObjectId(e.cost_code)
                    const cc = ccId != null ? costCodes[ccId] : undefined
                    const entrySt = e.adjusted_minutes_st ?? 0
                    return (
                        cc?.pto_bucket_id === record.pto_bucket_id &&
                        record.hours != null &&
                        entrySt >= Math.round(record.hours * 60)
                    )
                })
                rows.push({
                    request: record,
                    match: matchedEntry ?? null,
                    fulfilled: !!matchedEntry || !!ptoFulfillment[String(record.id)],
                    date: record.date,
                    cost_code: matchedEntry ? getObjectId(matchedEntry.cost_code) ?? null : null,
                    project: matchedEntry
                        ? matchedEntry.project ??
                          (typeof matchedEntry.cost_code === "object"
                              ? matchedEntry.cost_code?.project?.id ?? null
                              : null)
                        : null,
                    absence_type: null,
                    work_shift_id: matchedEntry?.work_shift_id ?? null,
                    gridId: record.id,
                })
            }

            return rows
        }, [] as PTORequestsTableRow[])

        return rows
    }, [employeePTO, employeeHolidays, costCodes, ptoFulfillment, localSourceData, employeesInContext, date])
}

/**
 * Extract PTO/Holiday records from entity maps that match the source employee(s) and date
 * encoded in the given source data rows.
 *
 * Used by both the Copy/Paste and Duplicate flows to identify which rows are PTO-driven.
 * The `sourceData` must be the original (pre-remap) rows so that the source date is intact.
 *
 * @param sourceData - Original source data rows encoding the source employee+date.
 * @param ptoObjects - EmployeePTO records from Redux entities keyed by ID.
 * @param holidayObjects - EmployeeHoliday records from Redux entities keyed by ID.
 * @returns PTO and holiday records for the source employee(s) on the source date.
 */
export const getSourcePTORecordsFromRows = (
    sourceData: tSourceData,
    ptoObjects: Record<number, EmployeePTORecord>,
    holidayObjects: Record<string, EmployeeHolidayRecord>
): Array<EmployeePTORecord | EmployeeHolidayRecord> => {
    const allRows: tResourceObject[] = [
        ...(sourceData.timekeepingEntries ?? []),
        ...(sourceData.absences ?? []),
        ...(sourceData.ewsStartStopTimes ?? []),
        ...(sourceData.employeeEntries ?? []),
    ]
    if (allRows.length === 0) return []

    const sourceEmployeeIds = new Set(
        allRows.map(r => getObjectId(r.employee)).filter((id): id is number => id != null)
    )
    const sourceDate = allRows.find(r => typeof r.date === "string")?.date as string | undefined
    if (!sourceDate || sourceEmployeeIds.size === 0) return []

    return [
        ...Object.values(ptoObjects).filter(r => r.date === sourceDate && sourceEmployeeIds.has(r.employee)),
        ...Object.values(holidayObjects).filter(r => r.date === sourceDate && sourceEmployeeIds.has(r.employee)),
    ]
}

/**
 * Given source data that has been remapped to a destination cell, strips any PTO-driven TK entries
 * and absences (identified via `sourcePTORecords` from the source cell's Redux context) and
 * replaces them with fresh rows built from the destination employee(s)' PTO/Holiday requests.
 *
 * Used by both the Copy/Paste and Duplicate flows so the destination cell always reflects the
 * correct PTO rows for its own employees and date rather than carrying over the source cell's PTO.
 *
 * Rules:
 *   - TK entries are PTO-driven if their `pto_bucket_id` (direct field) or their cost code's
 *     `pto_bucket_id` (looked up via `costCodes`) is non-null, regardless of which bucket.
 *   - Absences are PTO-driven if their `(employee, date, absence_type)` tuple matches any
 *     record in `sourcePTORecords` that carries a non-null `absence_type_id`.
 *   - Project IDs and PTO cost codes are collected from ALL source TK entries (including the
 *     stripped ones). Source entries' cost codes with a `pto_bucket_id` are pre-seeded into
 *     the bucket→CC map so the same PCC is reused when the bucket matches the destination.
 *   - COST_CODE entries with 0 resolvable cost codes are omitted (can't determine which to use).
 *   - Already-fulfilled PTO requests (per `ptoFulfillment`) are skipped.
 *
 * @param sourceData - Source data remapped to the destination cell.
 * @param sourcePTORecords - PTO/Holiday records from Redux for the source employee(s)/date.
 * @param destinationDate - Target date (YYYY-MM-DD).
 * @param costCodes - Cost code objects from Redux (keyed by ID) for pto_bucket_id lookup.
 * @param ptoFulfillment - Map of record ID → fulfilled boolean from the Redux selector.
 * @param includeUnresolvableCostCodeRows - When true, COST_CODE PTO entries are included even
 *   when no cost code can be resolved (cost_code will be null). Used in the Duplicate flow
 *   where the confirmation modal lets the user fill in missing cost codes.
 * @returns A new tSourceData with PTO-driven rows replaced by destination PTO rows.
 *   All source rows (PTO-driven and regular) are omitted for any employee who has PTO
 *   requests on the destination date; their PTO is handled separately by auto-creation or
 *   manual entry.
 */
export const applyPTORowsForPaste = async (
    sourceData: tSourceData,
    sourcePTORecords: Array<EmployeePTORecord | EmployeeHolidayRecord>,
    destinationDate: string,
    costCodes: Record<number, iCostCode>,
    ptoFulfillment: Record<string, boolean>,
    includeUnresolvableCostCodeRows = false
): Promise<tSourceData> => {
    const ptoDrivenAbsenceKeys = new Set<string>()
    for (const record of sourcePTORecords) {
        if (record.absence_type_id != null) {
            // Use destinationDate rather than record.date: sourceData absences are remapped to the
            // destination date before this function is called, so record.date (source date) would
            // never match absence.date when pasting across dates.
            ptoDrivenAbsenceKeys.add(`${record.employee}-${destinationDate}-${record.absence_type_id}`)
        }
    }

    const tkEntries = (sourceData.timekeepingEntries ?? []) as tResourceObject[]
    const absenceRows = (sourceData.absences ?? []) as tResourceObject[]

    const isTKEntryPTODriven = (entry: tResourceObject): boolean => {
        if (entry.pto_bucket_id != null) return true
        const ccId = getObjectId(entry.cost_code)
        if (ccId != null) {
            const cc = costCodes[ccId]
            if (cc?.pto_bucket_id != null) return true
        }
        return false
    }

    // Collect project IDs and PTO cost codes from ALL TK entries before filtering.
    const projectIds = [
        ...new Set(tkEntries.map(entry => getObjectId(entry.project)).filter((id): id is number => id != null)),
    ]
    const sourcePTOCostCodes: Array<{ id: number; pto_bucket_id: number }> = []
    const seenSourceCCIds = new Set<number>()
    for (const entry of tkEntries) {
        const ccId = getObjectId(entry.cost_code)
        if (ccId == null || seenSourceCCIds.has(ccId)) continue
        const cc = costCodes[ccId]
        if (cc?.pto_bucket_id != null) {
            seenSourceCCIds.add(ccId)
            sourcePTOCostCodes.push({ id: ccId, pto_bucket_id: cc.pto_bucket_id })
        }
    }

    // Collect all rows for employee extraction and base-field lookup (before filtering)
    const allRows: tResourceObject[] = [
        ...tkEntries,
        ...absenceRows,
        ...(sourceData.ewsStartStopTimes ?? []),
        ...(sourceData.employeeEntries ?? []),
    ]

    const destinationEmployeeIds = [
        ...new Set(allRows.map(r => getObjectId(r.employee)).filter((id): id is number => id != null)),
    ]

    // Filter PTO-driven rows from source data
    const filteredTKEntries = tkEntries.filter(entry => !isTKEntryPTODriven(entry))
    const filteredAbsences = absenceRows.filter(absence => {
        const atId = getObjectId(absence.absence_type)
        const empId = getObjectId(absence.employee)
        if (atId == null || empId == null) return true
        return !ptoDrivenAbsenceKeys.has(`${empId}-${absence.date as string}-${atId}`)
    })

    if (destinationEmployeeIds.length === 0) {
        return { ...sourceData, timekeepingEntries: filteredTKEntries, absences: filteredAbsences }
    }

    // Fetch PTO/Holiday records for the destination employee(s) and date
    let destPTORecords: EmployeePTORecord[] = []
    let destHolidayRecords: EmployeeHolidayRecord[] = []
    try {
        const [ptoResponse, holidayResponse] = await Promise.all([
            API.getV4Resources("employeePTO", {
                shift_start_time_0: destinationDate,
                shift_start_time_1: destinationDate,
                employee_id: destinationEmployeeIds,
            }) as Promise<{ results: EmployeePTORecord[] }>,
            API.getV4Resources("employeeHolidays", {
                shift_start_time_0: destinationDate,
                shift_start_time_1: destinationDate,
                employee_id: destinationEmployeeIds,
            }) as Promise<{ results: EmployeeHolidayRecord[] }>,
        ])
        destPTORecords = ptoResponse.results.filter(r => !ptoFulfillment[String(r.id)])
        destHolidayRecords = holidayResponse.results.filter(r => !ptoFulfillment[String(r.id)])
    } catch {
        // Non-fatal; proceed without adding destination PTO rows
        return { ...sourceData, timekeepingEntries: filteredTKEntries, absences: filteredAbsences }
    }

    const allDestPTORecords = [...destPTORecords, ...destHolidayRecords]
    if (allDestPTORecords.length === 0) {
        return { ...sourceData, timekeepingEntries: filteredTKEntries, absences: filteredAbsences }
    }

    // Employees with PTO/holiday requests on the destination date should not receive any
    // copied source data — their PTO is handled by auto-creation or manual entry.
    const employeesWithDestPTO = new Set(allDestPTORecords.map(r => r.employee))
    const excludeByPTO = (rows: tResourceObject[]): tResourceObject[] =>
        rows.filter(r => {
            const empId = getObjectId(r.employee)
            return empId == null || !employeesWithDestPTO.has(empId)
        })

    // Resolve cost codes for COST_CODE buckets; source PTO cost codes are pre-seeded so the
    // same PCC is reused when the bucket matches, skipping the API call for covered buckets.
    let ptoCostCodesByBucket: Map<number, Array<{ id: number }>> = new Map()
    if (projectIds.length > 0 || sourcePTOCostCodes.length > 0) {
        try {
            ptoCostCodesByBucket = await buildPTOCostCodesByBucket(
                allDestPTORecords,
                projectIds,
                sourcePTOCostCodes
            )
        } catch {
            // Non-fatal; COST_CODE rows with no match will simply be omitted
        }
    }

    // Group destination PTO records by employee
    const destPTOByEmployee = allDestPTORecords.reduce((map, r) => {
        if (!map[r.employee]) map[r.employee] = []
        map[r.employee].push(r)
        return map
    }, {} as Record<number, Array<EmployeePTORecord | EmployeeHolidayRecord>>)

    // Build a lookup of base fields per employee from the (pre-filter) source rows
    const baseFieldsByEmployee: Record<
        number,
        {
            foreman: number
            shiftStartTime: Date | null
            shiftEndTime: Date | null
            workShift: Record<string, unknown>
        }
    > = {}
    for (const row of allRows) {
        const empId = getObjectId(row.employee)
        if (empId == null || empId in baseFieldsByEmployee) continue
        baseFieldsByEmployee[empId] = {
            foreman: getObjectId(row.foreman) ?? 0,
            shiftStartTime: row.shift_start_time ? new Date(row.shift_start_time as string) : null,
            shiftEndTime: row.shift_end_time ? new Date(row.shift_end_time as string) : null,
            workShift: (row.work_shift_id as Record<string, unknown>) ?? {},
        }
    }

    // Build new PTO rows for each destination employee and merge them in
    const newTKEntries: tResourceObject[] = []
    const newAbsences: tResourceObject[] = []
    for (const employeeId of destinationEmployeeIds) {
        const ptoForEmployee = destPTOByEmployee[employeeId] ?? []
        if (ptoForEmployee.length === 0) continue
        const baseFields = baseFieldsByEmployee[employeeId]
        if (!baseFields) continue

        const { absences, timekeepingEntries } = buildPTORowsForEmployee(
            ptoForEmployee,
            baseFields,
            ptoCostCodesByBucket,
            includeUnresolvableCostCodeRows
        )
        newTKEntries.push(...(timekeepingEntries as tResourceObject[]))
        newAbsences.push(...(absences as tResourceObject[]))
    }

    return {
        ...sourceData,
        timekeepingEntries: [...excludeByPTO(filteredTKEntries), ...newTKEntries],
        absences: [...excludeByPTO(filteredAbsences), ...newAbsences],
        ewsStartStopTimes: excludeByPTO((sourceData.ewsStartStopTimes ?? []) as tResourceObject[]),
        employeeEntries: excludeByPTO((sourceData.employeeEntries ?? []) as tResourceObject[]),
    }
}

/**
 * Applies PTO row replacement for the Duplicate flow after work shifts have been resolved.
 *
 * Strips PTO-driven entries from `sourceData` (identified via `sourcePTORecords` captured
 * from the original source cell before date remapping) and replaces them with rows built from
 * the destination employees' PTO/holiday requests for `destinationDate`.
 *
 * Must be called AFTER work shift IDs are resolved so that new PTO rows inherit the correct
 * `work_shift_id` from their sibling entries in the destination cell.
 *
 * COST_CODE PTO entries are included even when no cost code can be automatically resolved
 * (cost_code will be null), because the confirmation modal lets the user fill them in.
 *
 * @param sourceData - Source data remapped to the destination date.
 * @param sourcePTORecords - PTO/holiday records from the source cell (pre-remap).
 * @param entities - Redux entities slice (costCodes, employeePTO, employeeHolidays).
 * @param exceptions - Redux exceptions slice (tkSummary for fulfillment computation).
 * @param destinationDate - Destination date string (YYYY-MM-DD).
 * @returns New sourceData with PTO rows replaced for the destination.
 */
export const prepareDuplicateSourceData = async (
    sourceData: tSourceData,
    sourcePTORecords: Array<EmployeePTORecord | EmployeeHolidayRecord>,
    entities:
        | {
              costCodes?: { objects?: Record<number, iCostCode> } | null
              employeePTO?: { objects?: Record<number, EmployeePTORecord> } | null
              employeeHolidays?: { objects?: Record<string, EmployeeHolidayRecord> } | null
          }
        | null
        | undefined,
    exceptions: { tkSummary?: TkSummary | null } | null | undefined,
    destinationDate: string
): Promise<tSourceData> => {
    if (!getFlagEnabled("WA-9197-pto-accruals")) {
        return sourceData
    }
    const costCodes = entities?.costCodes?.objects ?? {}
    const ptoFulfillment = computePTOFulfillment(
        entities?.employeePTO?.objects ?? {},
        entities?.employeeHolidays?.objects ?? {},
        exceptions?.tkSummary
    )
    return await applyPTORowsForPaste(
        sourceData,
        sourcePTORecords,
        destinationDate,
        costCodes,
        ptoFulfillment,
        true
    )
}

/**
 * Returns the column and grid configuration for the PTO Requests tab in the weekly TK detail modal.
 * This tab is a read-only reference view of PTO and holiday requests for the employees in context,
 * showing their fulfillment status and details. A "Fulfill Requests" row action allows the user to
 * copy request details as placeholder rows into the Timekeeping and Absences tabs.
 *
 * The Project, Hours Requested, and Absence Type columns are hidden by default; the modal
 * shows them selectively based on which PTO bucket types are present in the current row data.
 */
export const getPTORequestsTable = (_featureFlags: tFeatures): Record<string, unknown> => {
    return {
        tableName: "PTO Requests",
        id: TableIds.PTORequestsModal,
        navId: "pto-requests",
        resources: ["employeePTO", "employeeHolidays"],
        colDefs: [
            checkboxColDefCreator({}),
            {
                colId: "pto_fulfillment_status",
                headerName: "Fulfilled",
                field: "/fulfilled",
                width: 70,
                sortable: false,
                filter: false,
                cellRenderer: ({ data }: { data?: PTORequestsTableRow }) => {
                    if (!data) return null
                    return (
                        <div style={{ width: iconSizeM }}>
                            {data.fulfilled ? (
                                <IconPalmTreeFilled title="Fulfilled" className="toolbar-btn-icon" />
                            ) : (
                                <IconPalmTree title="Unfulfilled" className="toolbar-btn-icon" />
                            )}
                        </div>
                    )
                },
            },
            {
                ...employeeColDefCreator({}),
                field: "/request/employee",
            },
            stringColDefCreator({
                headerName: "Reason",
                field: "/reason",
                valueGetter: ({ data }: { data: PTORequestsTableRow }) => {
                    return (data.request as EmployeeHolidayRecord).name ?? "Employee Request"
                },
                width: 180,
            }),
            stringColDefCreator({
                headerName: "Bucket",
                field: "/request/pto_bucket_name",
                width: 180,
            }),
            {
                ...projectColDefCreator({}),
                colId: "pto_project",
                field: "/project",
                hide: true,
                required: false,
            },
            {
                ...numberColDefCreator({
                    headerName: "Hours Requested",
                    field: "/request/hours",
                    hide: true,
                    width: 150,
                }),
                colId: "pto_requested_hours",
            },
            {
                ...companyAbsenceTypeColDefCreator({}),
                colId: "pto_absence_type",
                headerName: "Absence Type",
                hide: true,
            },
        ],
        gridSettings: {
            defaultColDef: {
                filter: false,
                editable: false,
            },
            rowSelection: "multiple",
            suppressRowClickSelection: true,
            // Only unfulfilled rows may be selected; fulfilled ones are already satisfied.
            isRowSelectable: (rowNode: { data?: PTORequestsTableRow }) => !rowNode.data?.fulfilled,
        },
        otherSettings: {
            filterByLockedColumns: true,
            hideLockedColumns: true,
            isInModalTab: true,
            suppressExportButton: true,
            highlightErrorRows: true,
            buttons: {
                cell: [],
                row: [
                    {
                        label: "Fulfill Requests",
                        icon: "fulfillPTO",
                        action: "fulfillPTORequests",
                        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                        disabled: (context: { selectedRows: PTORequestsTableRow[] }) =>
                            !context.selectedRows.some(r => !r.fulfilled),
                        tooltip: () =>
                            "Add timekeeping entries to the Timekeeping and Absences tabs" +
                            " according to the details of the selected requests.",
                    },
                ],
                table: [
                    {
                        label: "Fulfill Requests",
                        icon: "fulfillPTO",
                        action: "fulfillAllPTORequests",
                        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                        disabled: (_context: unknown, sourceData: Record<string, PTORequestsTableRow[]>) =>
                            !Object.values(sourceData)
                                .flat()
                                .some(r => !r.fulfilled),
                        tooltip: () =>
                            "Add timekeeping entries to the Timekeeping and Absences tabs" +
                            " for all PTO requests shown in this tab.",
                    },
                ],
            },
        },
    }
}
